import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../components/CustomButton';
import CustomModal from '../../common/CustomModal';

interface ReasonFieldProps {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
}

// Reason textarea shared by the release and capture modals; styling mirrors
// the deposit amount input.
export const ReasonField = ({ id, label, value, onChange }: ReasonFieldProps) => (
    <div className={'flex flex-col gap-1'}>
        <label className={'text-sm font-medium text-foreground'} htmlFor={id}>
            {label}
        </label>
        <textarea
            id={id}
            rows={3}
            className={
                'w-full border border-foreground/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary'
            }
            value={value}
            onChange={(e) => onChange(e.target.value)}
        />
    </div>
);

interface ShellProps {
    title: string;
    description: string;
    confirmLabel: string;
    confirmDisabled?: boolean;
    loading: boolean;
    onConfirm: () => void;
    onClose: () => void;
    children: ReactNode;
}

// Common frame of the deposit action modals: CustomModal with cancel and
// confirm buttons; the fields render as children.
const DepositModalShell = ({
    title,
    description,
    confirmLabel,
    confirmDisabled = false,
    loading,
    onConfirm,
    onClose,
    children,
}: ShellProps) => {
    const { t } = useTranslation();

    return (
        <CustomModal
            open={true}
            title={title}
            description={description}
            onClose={onClose}
            closeButton={
                <CustomButton title={t('common.cancel')} isFilled={false} onClick={onClose} />
            }
            saveButton={
                <CustomButton
                    title={confirmLabel}
                    loading={loading}
                    disabled={confirmDisabled || loading}
                    onClick={onConfirm}
                />
            }
        >
            <div className={'flex flex-col gap-3'}>{children}</div>
        </CustomModal>
    );
};

export default DepositModalShell;
